import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { isDeadFormRoute, resolveFormRoute } from '../lib/field-forms';
import type { FieldForm, FormGroup } from './useFieldForms';

interface AvailableForm extends FieldForm {
  isDefault?: boolean;
}

interface ApiResponse {
  success: boolean;
  data: { groups: Array<{ label: string; vertical?: string; forms: AvailableForm[] }> };
}

/**
 * Shown on a template the tenant published with nothing on it. It is a real
 * state — `createTemplate` writes the row before any field exists, and an
 * extraction can be approved with an empty schema — and the form library screen
 * already says the same thing to the manager who can fix it.
 */
export const NO_FIELDS_LABEL = 'No fields yet';

/**
 * The forms ANY field user may open, whatever verticals their tenant has.
 *
 * WHY NOT `useFieldForms`
 * ----------------------
 * That hook calls `/field/forms`, and every `/api/v1/field/*` route resolves a
 * `trucking_drivers` row first. Casing tenants have none, so a casing hand
 * opening a Forms tab backed by it gets "No driver record linked to this
 * account" — a refusal, not an empty list. `/forms/available` answers the same
 * question scoped to the COMPANY, which is what a list of form templates
 * actually is, and returns `getFormsForDriver`'s shape group for group so the
 * same card renders both.
 *
 * The extra key that matters here is `fieldCount`. A template with none of them
 * draws an empty page with a Submit button on it, so the row is disabled with
 * the reason on it rather than routed at.
 */
export function useAvailableForms() {
  return useQuery<FormGroup[]>({
    queryKey: ['forms-available'],
    queryFn: async () => {
      const r = await api.forms.available();
      const apiBody = r.data as ApiResponse;
      const rawGroups = apiBody?.data?.groups ?? [];
      return rawGroups.map((g) => ({
        group: g.label,
        label: g.label,
        vertical: g.vertical,
        forms: (g.forms ?? []).map((f) => ({
          ...f,
          route: resolveFormRoute(f.route, f.id),
          comingSoon: f.comingSoon || isDeadFormRoute(f.route) || undefined,
          // `fieldCount` is a count, so an absent one is NOT zero — a missing
          // key means the server did not say, and disabling a form over a key
          // that never arrived takes a working form away from a crew. Only a
          // number that really is 0 disables the row.
          disabledReason: f.fieldCount === 0 ? NO_FIELDS_LABEL : undefined,
        })),
      }));
    },
    staleTime: 300_000,
  });
}
